import React from 'react';

import { Group, Paper, Stack, Text, Title } from '@mantine/core';
import InvoiceStatusBadge from 'components/invoices/InvoiceStatusBadge';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import { InvoiceRetrieve } from 'types';
import { getPaymentMethodName } from 'utils/formatters';

interface InvoiceSummaryCardProps {
  invoice: InvoiceRetrieve;
}

const InvoiceSummaryCard = ({ invoice }: InvoiceSummaryCardProps): JSX.Element => {
  const { copy, formatCurrency, dateFormat, isTaxInclusive } = useLocalization();

  const subtotal = invoice.pretaxTotal / 100;
  const total = invoice.total / 100;
  const taxAmount = invoice.tax / 100;
  const discountAmount = invoice.invoiceItems.reduce((sum, iv) => sum + (iv.discountAmount ?? 0), 0) / 100;
  const isDiscounted = discountAmount > 0;
  const showSubtotalStrikethrough = isDiscounted && !isTaxInclusive;
  const showTotalStrikethrough = isDiscounted && isTaxInclusive;
  const originalSubtotal = subtotal + discountAmount;
  const originalTotal = total + discountAmount;

  return (
    <Paper
      withBorder
      m='auto'
      px='xl'
      pt='md'
      pb='xl'
      miw={'70%'}
      shadow='xs'
      style={{ whiteSpace: 'nowrap' }}
    >
      <Stack>
        <Group position='apart' spacing='xl' noWrap>
          <Title order={4}>Invoice Summary</Title>
          <div>
            <InvoiceStatusBadge status={invoice.status} />
          </div>
        </Group>
        <Stack spacing='xs'>
          <Group position='apart' noWrap>
            <Text color='dimmed'>Athlete</Text>
            <Text>{invoice.athlete.fullName}</Text>
          </Group>
          <Group position='apart' noWrap align='start'>
            <Text color='dimmed'>Items</Text>
            <Stack spacing={0} align='flex-end'>
              {invoice.invoiceItems.map((invoiceItem, i) => (
                <Text key={invoiceItem.invoiceItemId ?? i}>
                  {invoiceItem.quantity === 1
                    ? invoiceItem.productName
                    : `(${invoiceItem.quantity}) ${invoiceItem.productName}`}
                </Text>
              ))}
            </Stack>
          </Group>
          <Group position='apart' noWrap align='start'>
            <Text color='dimmed'>{copy.financial.subtotal}</Text>
            <Stack spacing={0} align='flex-end'>
              {showSubtotalStrikethrough && (
                <Text strikethrough color='dimmed' size='sm'>
                  {formatCurrency(originalSubtotal)}
                </Text>
              )}
              <Text color={showSubtotalStrikethrough ? 'green' : undefined}>{formatCurrency(subtotal)}</Text>
            </Stack>
          </Group>
          {isDiscounted && (
            <Group position='apart' noWrap>
              <Text color='dimmed'>Discount</Text>
              <Text color='green'>−{formatCurrency(discountAmount)}</Text>
            </Group>
          )}
          <Group position='apart' noWrap>
            <Text color='dimmed'>{copy.financial.tax}</Text>
            <Text>{formatCurrency(taxAmount)}</Text>
          </Group>
          <Group position='apart' noWrap align='start'>
            <Text color='dimmed' fw={700}>
              Total
            </Text>
            <Stack spacing={0} align='flex-end'>
              {showTotalStrikethrough && (
                <Text strikethrough color='dimmed' size='sm' fw={500}>
                  {formatCurrency(originalTotal)}
                </Text>
              )}
              <Text fw={700} color={showTotalStrikethrough ? 'green' : undefined}>
                {formatCurrency(total)}
              </Text>
            </Stack>
          </Group>
          <Group position='apart' noWrap>
            <Text color='dimmed'>Due Date</Text>
            <Text>{dayjs(invoice.dueDate).format(dateFormat)}</Text>
          </Group>
          {invoice.paymentDate && (
            <Group position='apart' noWrap>
              <Text color='dimmed'>Payment Date</Text>
              <Text>{dayjs(invoice.paymentDate).format(dateFormat)}</Text>
            </Group>
          )}
          <Group position='apart' noWrap>
            <Text color='dimmed'>Payment Method</Text>
            <Text>
              {invoice.paymentMethod
                ? getPaymentMethodName(invoice.paymentMethod)
                : invoice.nonStripePaymentMethod}
            </Text>
          </Group>
          <Group position='apart' noWrap>
            <Text color='dimmed'>Auto-Pay</Text>
            <Text>{invoice.autoPay ? 'On' : 'Off'}</Text>
          </Group>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default InvoiceSummaryCard;
